import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Settings, Sparkles } from "lucide-react";
import { toast } from "sonner";

import CarregandoRelatorio from "../../relatorios-ofertas/comum/Carregando";

import { Button } from "../../../components/ui/button";

import { carregarRelatorio } from "./api";
import BarrasPorDia from "./BarrasPorDia";
import { ROTA_CONFIG, ROTA_HUB_RELATORIOS } from "./rotas";
import { reais } from "./formato";
import TabelaRanking from "./TabelaRanking";
import { formatarQtd } from "../../../lib/unidadeVenda";
import { CarregandoBloco, CarregandoCards } from "../../../components/carregando";
import { Skeleton } from "../../../components/ui/skeleton";

const PERIODOS = [7, 30, 90];
const hojeMenos = (dias) => { const d = new Date(); d.setDate(d.getDate() - dias); return d.toLocaleDateString("sv-SE"); };
const fmtTempo = (s) => (s == null ? "—" : `${Math.floor(s / 60)}min ${String(s % 60).padStart(2, "0")}s`);
const fmtPct = (v) => (v == null ? "—" : `${String(v).replace(".", ",")}%`);

function Numero({ rotulo, valor, detalhe }) {
  return (
    <div className="rounded-xl border bg-card p-4">
      <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{rotulo}</p>
      <p className="text-2xl font-black tabular-nums mt-0.5">{valor}</p>
      {detalhe && <p className="text-[11px] text-muted-foreground mt-0.5">{detalhe}</p>}
    </div>
  );
}

// Relatórios → Relatório de Ofertas → Campanhas → Campanha Inteligente.
export default function RelatorioCampanhaInteligentePage() {
  const navigate = useNavigate();
  const [dias, setDias] = useState(30);
  const [rel, setRel] = useState(null);
  // Erro na 1ª leitura: aviso com "Tentar de novo" (antes piscava para sempre);
  // trocar o período mantém os dados na tela até os novos chegarem (25/10/2026).
  const [erro, setErro] = useState(false);
  const [tentativa, setTentativa] = useState(0);

  useEffect(() => {
    let vivo = true;
    setErro(false);
    carregarRelatorio({ inicio: hojeMenos(dias - 1), fim: hojeMenos(0) })
      .then((d) => { if (vivo) setRel(d); })
      .catch(() => { if (vivo) { setErro(true); toast.error("Não foi possível carregar o relatório"); } });
    return () => { vivo = false; };
  }, [dias, tentativa]);

  const tempo = useMemo(() => {
    const serie = rel?.tempo_por_dia || [];
    const media = (lista) => (lista.length ? Math.round(lista.reduce((s, d) => s + d.media_segundos, 0) / lista.length) : null);
    const antes = rel?.ligada_em ? serie.filter((d) => d.dia < rel.ligada_em) : [];
    const depois = rel?.ligada_em ? serie.filter((d) => d.dia >= rel.ligada_em) : serie;
    return { serie, antes: media(antes), depois: media(depois) };
  }, [rel]);
  const novos = (rel?.cadastros_por_dia || []).reduce((s, d) => s + d.novos, 0);

  return (
    <div className="p-4 sm:p-6">
      <div className="max-w-5xl mx-auto space-y-6" data-e2e="relatorio-campanha-inteligente">
        <div>
          <button type="button" onClick={() => navigate(ROTA_HUB_RELATORIOS)}
            className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-3">
            <ArrowLeft className="w-4 h-4" /> Relatório de Campanhas
          </button>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-purple-500/10 rounded-xl flex items-center justify-center">
                <Sparkles className="w-6 h-6 text-purple-500" strokeWidth={1.5} />
              </div>
              <div>
                <h1 className="text-2xl font-bold">Relatório da Campanha Inteligente</h1>
                {rel ? (
                  <p className="text-sm text-muted-foreground">{(rel.is_active ? "Campanha ligada" : "Campanha desligada — histórico abaixo")}</p>
                ) : <Skeleton className="h-4 w-48 mt-1" />}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex rounded-lg border p-0.5 bg-card">
                {PERIODOS.map((p) => (
                  <button key={p} type="button" onClick={() => setDias(p)}
                    className={`px-2.5 py-1 text-xs font-semibold rounded-md ${dias === p ? "bg-purple-500 text-white" : "text-muted-foreground hover:text-foreground"}`}>
                    {p} dias
                  </button>
                ))}
              </div>
              <Button variant="outline" size="sm" onClick={() => navigate(ROTA_CONFIG)}>
                <Settings className="w-4 h-4 mr-1.5" /> Configurar
              </Button>
            </div>
          </div>
        </div>

        {!rel && erro ? <CarregandoRelatorio erro onTentar={() => setTentativa((t) => t + 1)} /> : !rel ? (
          // Carregamento com cards (25/10/2026): números, a caça e os dois gráficos piscando.
          <div className="space-y-6" aria-busy="true" aria-label="Carregando">
            <CarregandoCards quantos={3} className="grid grid-cols-1 sm:grid-cols-3 gap-3" />
            <CarregandoCards quantos={4} className="grid grid-cols-2 sm:grid-cols-4 gap-3" />
            <div className="grid sm:grid-cols-2 gap-6"><CarregandoBloco altura="h-56" /><CarregandoBloco altura="h-56" /></div>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Numero rotulo="Pedidos com oferta" valor={rel.vendas.pedidos} />
              <Numero rotulo="Faturado nesses pedidos" valor={reais(rel.vendas.faturado)} />
              <Numero rotulo="Desconto concedido" valor={reais(rel.vendas.desconto)} />
            </div>
            <section>
              <h2 className="text-sm font-semibold mb-2">A caça</h2>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <Numero rotulo="Sorteadas" valor={rel.caca.sorteadas} detalhe="ofertas entregues a clientes" />
                <Numero rotulo="Vistas" valor={rel.caca.vistas} detalhe="cards que entraram na tela" />
                <Numero rotulo="Vendidas" valor={formatarQtd(rel.caca.convertidas, null)} detalhe="ofertas vendidas (produto a peso conta 1)" />
                <Numero rotulo="Aproveitamento" valor={fmtPct(rel.caca.aproveitamento_pct)} detalhe="vendidas a cada 100 vistas" />
              </div>
            </section>
            <div className="grid sm:grid-cols-2 gap-6">
              <section className="rounded-xl border bg-card p-4">
                <h2 className="text-sm font-semibold">Cadastros novos por dia</h2>
                <p className="text-xs text-muted-foreground mb-3">{novos} no período — o banco que fica com a loja.</p>
                <BarrasPorDia serie={rel.cadastros_por_dia} valor={(d) => d.novos} rotulo="Cadastros novos por dia" />
              </section>
              <section className="rounded-xl border bg-card p-4">
                <h2 className="text-sm font-semibold">Tempo médio no cardápio</h2>
                <p className="text-xs text-muted-foreground mb-3">
                  {rel.ligada_em ? <>Antes de ligar: <b>{fmtTempo(tempo.antes)}</b> · depois: <b>{fmtTempo(tempo.depois)}</b></> : <>Média: <b>{fmtTempo(tempo.depois)}</b></>}
                </p>
                <BarrasPorDia serie={tempo.serie} valor={(d) => d.media_segundos} rotulo="Tempo médio no cardápio por dia" destacarDesde={rel.ligada_em} />
              </section>
            </div>
            <TabelaRanking ranking={rel.ranking} />
            <p className="text-xs text-muted-foreground border-t pt-3">{rel.ressalva}</p>
          </>
        )}
      </div>
    </div>
  );
}
